import React, { useState, useEffect, useCallback } from "react";
import {
  getPayouts,
  approvePayoutRequest,
  rejectPayoutRequest,
  getReimbursements,
  getRefunds,
  getAllPenalties,
  waiveScholarPenalty,
} from "../services/adminService";
import {
  pageContainer,
  cardStyle,
  statsGrid,
  labelStyle,
  valueStyle,
  subTextStyle,
  sectionTitle,
  thStyle,
  tdStyle,
  badgeStyle,
  tableSearch,
  smallSelect,
} from "../styles/theme";
import { useToast } from "../utils/useAdminPage";
import Paginator from "../components/Paginator";
import Toast from "../components/Toast";
import PageHeader from "../components/PageHeader";

// ── Types ────────────────────────────────────────────────────────────────────


// ── Process Payout Modal ─────────────────────────────────────────────────────
interface PayoutModalProps {
  payout: any | null;
  onConfirm: (action: "approve" | "reject", reason?: string, customAmount?: number) => Promise<void>;
  onCancel: () => void;
  processing: boolean;
}

const ProcessPayoutModal: React.FC<PayoutModalProps> = ({
  payout,
  onConfirm,
  onCancel,
  processing,
}) => {
  const [reason, setReason] = useState("");
  const originalRequestedAmount = payout?.originalAmount ?? payout?.amount ?? 0;
  const [editedAmountStr, setEditedAmountStr] = useState<string>("");

  useEffect(() => {
    if (payout) {
      setEditedAmountStr(String(payout.amount || 0));
      setReason("");
    }
  }, [payout]);

  if (!payout) return null;

  const editedNum = parseFloat(editedAmountStr);
  const isInvalidAmount = isNaN(editedNum) || editedNum <= 0;

  let validationError = "";
  if (isNaN(editedNum) || editedNum <= 0) {
    validationError = "Approved amount must be greater than ₹0";
  }

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15,23,42,0.55)",
        backdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
      }}
      onClick={onCancel}
    >
      <div
        style={{
          background: "hsl(var(--card))",
          borderRadius: 20,
          padding: "36px 40px",
          width: 460,
          maxWidth: "92vw",
          boxShadow: "0 24px 80px rgba(0,0,0,0.18)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ textAlign: "center", marginBottom: 20 }}>
          <div
            style={{
              width: 64,
              height: 64,
              borderRadius: "50%",
              background: "linear-gradient(135deg,#eff6ff,#dbeafe)",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 28,
              boxShadow: "0 0 0 8px #eff6ff",
            }}
          >
            ₹
          </div>
        </div>
        <h2
          style={{
            margin: 0,
            fontSize: 20,
            fontWeight: 800,
            color: "hsl(var(--foreground))",
            textAlign: "center",
          }}
        >
          Process Payout Request
        </h2>
        <p
          style={{
            margin: "10px 0 24px",
            fontSize: 14,
            color: "#64748b",
            textAlign: "center",
            lineHeight: 1.6,
          }}
        >
          Review and approve or adjust the payout amount. Admin can adjust (increase or decrease) the payout amount before processing.
        </p>
        <div
          style={{
            background: "#f8fafc",
            border: "1px solid hsl(var(--border))",
            borderRadius: 14,
            padding: "16px 20px",
            marginBottom: 20,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: 10,
            }}
          >
            <span
              style={{
                fontSize: 12,
                color: "#94a3b8",
                fontWeight: 600,
                textTransform: "uppercase",
              }}
            >
              Scholar
            </span>
            <span
              style={{
                fontSize: 14,
                fontWeight: 700,
                color: "hsl(var(--foreground))",
              }}
            >
              {payout.scholar?.user?.name || "Unknown Scholar"}
            </span>
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 12,
            }}
          >
            <span
              style={{
                fontSize: 12,
                color: "#94a3b8",
                fontWeight: 600,
                textTransform: "uppercase",
              }}
            >
              Requested Amount
            </span>
            <span style={{ fontSize: 15, fontWeight: 700, color: "#475569" }}>
              ₹{originalRequestedAmount.toLocaleString()}
            </span>
          </div>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 4,
              marginBottom: 12,
            }}
          >
            <label
              style={{
                fontSize: 11,
                color: "#475569",
                fontWeight: 700,
                textTransform: "uppercase",
              }}
            >
              Approved Payout Amount (₹)
            </label>
            <input
              type="number"
              min="1"
              step="any"
              value={editedAmountStr}
              onChange={(e) => setEditedAmountStr(e.target.value)}
              style={{
                width: "100%",
                padding: "8px 12px",
                borderRadius: 8,
                border: validationError ? "1.5px solid #ef4444" : "1px solid #cbd5e1",
                fontSize: 16,
                fontWeight: 800,
                color: validationError ? "#ef4444" : "#2563eb",
                background: "#fff",
                outline: "none",
                boxSizing: "border-box",
              }}
            />
            {validationError ? (
              <span style={{ fontSize: 11, color: "#ef4444", fontWeight: 600, marginTop: 2 }}>
                ⚠️ {validationError}
              </span>
            ) : editedNum !== originalRequestedAmount && !isNaN(editedNum) && editedNum > 0 ? (
              <span style={{ fontSize: 11, color: "#059669", fontWeight: 600, marginTop: 2 }}>
                ✓ Amount adjusted: ₹{editedNum.toLocaleString()} (Requested: ₹{originalRequestedAmount.toLocaleString()})
              </span>
            ) : null}
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: 10,
            }}
          >
            <span
              style={{
                fontSize: 12,
                color: "#94a3b8",
                fontWeight: 600,
                textTransform: "uppercase",
              }}
            >
              Processing Fee
            </span>
            <span style={{ fontSize: 12, fontWeight: 600, color: "#ef4444" }}>
              - ₹{(payout.processingFee || 0).toLocaleString()}
            </span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <span
              style={{
                fontSize: 12,
                color: "#94a3b8",
                fontWeight: 600,
                textTransform: "uppercase",
              }}
            >
              Payout ID
            </span>
            <span
              style={{
                fontSize: 12,
                fontFamily: "monospace",
                color: "hsl(var(--muted-foreground))",
              }}
            >
              #{payout.id?.slice(0, 12).toUpperCase()}
            </span>
          </div>
        </div>
        <div style={{ marginBottom: 24 }}>
          <input
            type="text"
            placeholder="Rejection reason (optional)"
            style={{ ...tableSearch, width: "100%", boxSizing: "border-box" }}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </div>
        <div style={{ display: "flex", gap: 12 }}>
          <button
            onClick={() => onConfirm("reject", reason)}
            disabled={processing}
            style={{
              flex: 1,
              padding: "12px 0",
              borderRadius: 12,
              border: "1.5px solid #ef4444",
              background: "transparent",
              color: "#ef4444",
              fontWeight: 700,
              fontSize: 14,
              cursor: processing ? "not-allowed" : "pointer",
              opacity: processing ? 0.6 : 1,
            }}
          >
            Reject
          </button>
          <button
            onClick={() => onConfirm("approve", undefined, editedNum)}
            disabled={processing || isInvalidAmount}
            style={{
              flex: 2,
              padding: "12px 0",
              borderRadius: 12,
              border: "none",
              background: (processing || isInvalidAmount)
                ? "#93c5fd"
                : "linear-gradient(135deg,#2563eb,#3b82f6)",
              color: "#fff",
              fontWeight: 700,
              fontSize: 14,
              cursor: (processing || isInvalidAmount) ? "not-allowed" : "pointer",
              opacity: (processing || isInvalidAmount) ? 0.6 : 1,
              boxShadow: (processing || isInvalidAmount)
                ? "none"
                : "0 4px 16px rgba(37,99,235,0.35)",
            }}
          >
            {processing ? "Processing…" : "Approve Transfer"}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Finance Detail Modal ─────────────────────────────────────────────────────
interface FinanceDetailModalProps {
  item: any | null;
  type: "payout" | "reimbursement" | "refund" | null;
  onClose: () => void;
}

const FinanceDetailModal: React.FC<FinanceDetailModalProps> = ({
  item,
  type,
  onClose,
}) => {
  if (!item || !type) return null;

  const getStatusColor = (status: string) => {
    if (status === "PROCESSED" || status === "SUCCESS" || status === "COMPLETED")
      return { bg: "#f0fdf4", color: "#15803d" };
    if (status === "PENDING" || status === "PROCESSING")
      return { bg: "#fffbeb", color: "#b45309" };
    if (status === "FAILED" || status === "REJECTED")
      return { bg: "#fef2f2", color: "#b91c1c" };
    return { bg: "#f1f5f9", color: "#475569" };
  };

  const sc = getStatusColor(item.status || "PENDING");

  const renderSection = (label: string, value: React.ReactNode, sub?: string) => (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 14, fontWeight: 600, color: "#1e293b" }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>{sub}</div>}
    </div>
  );

  return (
    <div
      style={{
        position: "fixed", inset: 0, background: "rgba(15,23,42,0.4)",
        backdropFilter: "blur(6px)", display: "flex", alignItems: "center",
        justifyContent: "center", zIndex: 10000,
        animation: "fadeIn 0.2s ease-out"
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: "#fff", borderRadius: 24, padding: "32px",
          width: 480, maxWidth: "90vw", boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.15)",
          position: "relative"
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          style={{
            position: "absolute", top: 20, right: 20, border: "none",
            background: "#f1f5f9", width: 32, height: 32, borderRadius: "50%",
            display: "flex", alignItems: "center", justifyContent: "center",
            cursor: "pointer", color: "#64748b", transition: "all 0.2s"
          }}
        >
          ✕
        </button>

        <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 24 }}>
          <div style={{
            width: 48, height: 48, borderRadius: 12,
            background: type === "refund" ? "#fef2f2" : type === "reimbursement" ? "#f5f3ff" : "#eff6ff",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 20
          }}>
            {type === "payout" ? "📤" : type === "reimbursement" ? "💸" : "↩"}
          </div>
          <div>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: "#1e293b" }}>
              {type === "payout" ? "Payout Details" : type === "reimbursement" ? "Reimbursement Details" : "Refund Details"}
            </h2>
            <div style={{ fontSize: 12, color: "#94a3b8", fontWeight: 500 }}>
              Transaction ID: <span style={{ fontFamily: "monospace" }}>#{item.id?.slice(0, 12).toUpperCase()}</span>
            </div>
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px 24px", padding: "20px", background: "#f8fafc", borderRadius: 16, marginBottom: 24 }}>
          {renderSection("Status", <span style={{ ...badgeStyle, background: sc.bg, color: sc.color, margin: 0 }}>{item.status}</span>)}
          {renderSection("Date", item.createdAt ? new Date(item.createdAt).toLocaleString("en-IN", { dateStyle: "medium" }) : "—")}
          {renderSection("Amount", <span style={{ color: type === "refund" ? "#ef4444" : "#2563eb", fontSize: 18, fontWeight: 800 }}>₹{(item.amount || 0).toLocaleString()}</span>)}
          {type === "payout" && renderSection("Fee", `₹${(item.processingFee || 0).toLocaleString()}`)}
        </div>

        <div style={{ padding: "4px 8px" }}>
          {type === "payout" && (
            <>
              {renderSection("Scholar", item.scholar?.user?.name, item.scholar?.user?.email)}
              {item.originalAmount && item.originalAmount > item.amount && (
                renderSection("Original Requested Amount", `₹${item.originalAmount.toLocaleString()}`)
              )}
              {item.note && renderSection("Note", item.note)}
            </>
          )}

          {type === "reimbursement" && (
            <>
              {renderSection("Scholar", item.scholar?.user?.name)}
              {renderSection("Booking Reference", `Booking #${item.booking?.id?.slice(0, 8)}`, item.booking?.service?.name)}
              {item.note && renderSection("Reason", item.note)}
            </>
          )}

          {type === "refund" && (
            <>
              {renderSection("Customer", item.booking?.customer?.user?.name, item.booking?.customer?.user?.email)}
              {renderSection("Refund Type", item.refundType?.replace(/_/g, " "))}
              {renderSection("Booking Reference", `Booking #${item.booking?.id?.slice(0, 8)}`)}
              {item.razorpayRefundId && renderSection("Razorpay ID", item.razorpayRefundId)}
              {item.reason && renderSection("Reason", item.reason)}
            </>
          )}
        </div>

        <button
          onClick={onClose}
          style={{
            width: "100%", marginTop: 12, padding: "12px", borderRadius: 12,
            background: "#1e293b", color: "#fff", border: "none",
            fontWeight: 700, fontSize: 14, cursor: "pointer"
          }}
        >
          Close
        </button>
      </div>
    </div>
  );
};

// ── Payouts & Refunds Page ────────────────────────────────────────────────────
const PayoutsRefunds = () => {
  const [payouts, setPayouts] = useState<any[]>([]);
  const [reimbursements, setReimbursements] = useState<any[]>([]);
  const [refundsList, setRefundsList] = useState<any[]>([]);
  const [penaltiesList, setPenaltiesList] = useState<any[]>([]);

  const [loading, setLoading] = useState(true);

  const [pendingPayout, setPendingPayout] = useState<any | null>(null);
  const [processingId, setProcessingId] = useState<string | null>(null);

  const [detailItem, setDetailItem] = useState<any | null>(null);
  const [detailType, setDetailType] = useState<"payout" | "reimbursement" | "refund" | null>(null);

  const [activeTab, setActiveTab] = useState<
    "payouts" | "reimbursements" | "refunds" | "penalties"
  >("payouts");

  const [payoutFilter, setPayoutFilter] = useState("");
  const [payoutPage, setPayoutPage] = useState(1);

  const [reimbursementPage, setReimbursementPage] = useState(1);
  const [refundPage, setRefundPage] = useState(1);
  const [penaltyPage, setPenaltyPage] = useState(1);

  const pageSize = 10;
  const { toast, showToast } = useToast();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [pts, reims, refs, pens] = await Promise.all([
        getPayouts({ type: "EARNINGS" }),
        getReimbursements(),
        getRefunds(),
        getAllPenalties(),
      ]);
      setPayouts(pts || []);
      setReimbursements(reims || []);
      setRefundsList(refs || []);
      setPenaltiesList(pens || []);
    } catch {
      showToast("Failed to load financial data", "error");
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleProcessPayout = useCallback(
    async (action: "approve" | "reject", reason?: string, customAmount?: number) => {
      if (!pendingPayout) return;
      setProcessingId(pendingPayout.id);
      try {
        if (action === "approve") {
          await approvePayoutRequest(pendingPayout.id, customAmount);
          const finalAmt = customAmount ?? pendingPayout.amount ?? 0;
          showToast(
            `Payout of ₹${finalAmt.toLocaleString()} approved successfully.`,
          );
        } else {
          await rejectPayoutRequest(
            pendingPayout.id,
            reason || "Rejected by Admin",
          );
          showToast(`Payout rejected.`, "error");
        }
        setPendingPayout(null);
        await fetchData();
      } catch (err: any) {
        const errMsg = err?.response?.data?.message || err?.message || "Failed to process payout.";
        showToast(errMsg, "error");
      } finally {
        setProcessingId(null);
      }
    },
    [pendingPayout, showToast, fetchData],
  );

  const handleWaivePenalty = useCallback(
    async (penaltyId: string) => {
      const reason = prompt("Reason for waiving scholar penalty:");
      if (reason === null) return;
      try {
        await waiveScholarPenalty(penaltyId, reason || "Waived by Admin");
        showToast("Scholar penalty waived successfully");
        await fetchData();
      } catch {
        showToast("Failed to waive penalty", "error");
      }
    },
    [showToast, fetchData],
  );

  const filteredPayouts = payouts.filter((p) => {
    if (!payoutFilter) return true;
    return p.status === payoutFilter;
  });

  const pagedPayouts = filteredPayouts.slice(
    (payoutPage - 1) * pageSize,
    payoutPage * pageSize,
  );
  const pagedReimbursements = reimbursements.slice(
    (reimbursementPage - 1) * pageSize,
    reimbursementPage * pageSize,
  );
  const pagedRefunds = refundsList.slice(
    (refundPage - 1) * pageSize,
    refundPage * pageSize,
  );
  const pagedPenalties = penaltiesList.slice(
    (penaltyPage - 1) * pageSize,
    penaltyPage * pageSize,
  );

  const pendingPayoutsTotal = payouts
    .filter((p) => p.status === "PENDING")
    .reduce((a, c) => a + c.amount, 0);
  const processedTotal = payouts
    .filter((p) => p.status === "PROCESSED")
    .reduce((a, c) => a + c.amount, 0);

  const payoutStatusColor = (status: string) => {
    if (status === "PROCESSED" || status === "SUCCESS")
      return { bg: "#f0fdf4", color: "#15803d" };
    if (status === "PENDING" || status === "PROCESSING")
      return { bg: "#fffbeb", color: "#b45309" };
    if (status === "FAILED" || status === "REJECTED")
      return { bg: "#fef2f2", color: "#b91c1c" };
    return { bg: "#f1f5f9", color: "#475569" };
  };

  const getRefundTypeLabel = (type: string) => {
    switch (type) {
      case "FULL":
        return "Full Refund";
      case "BASE_TRAVEL":
        return "Base + Travel";
      case "BASE_TRAVEL_MINUS_10":
        return "Base + Travel (-10%)";
      case "BASE_TRAVEL_CUSTOM":
        return "Custom %";
      default:
        return type;
    }
  };

  const tab = (
    key: "payouts" | "reimbursements" | "refunds" | "penalties",
    label: string,
  ) => (
    <button
      key={key}
      onClick={() => setActiveTab(key)}
      style={{
        padding: "12px 20px",
        fontSize: 13,
        fontWeight: activeTab === key ? 700 : 500,
        color: activeTab === key ? "#2563eb" : "#64748b",
        cursor: "pointer",
        borderBottom:
          activeTab === key ? "2.5px solid #2563eb" : "2.5px solid transparent",
        background: "none",
        border: "none",
        outline: "none",
        whiteSpace: "nowrap",
        transition: "all .15s",
      }}
    >
      {label}
    </button>
  );

  return (
    <div style={pageContainer}>
      <style>{`
        @keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }
        @keyframes spin { to { transform: rotate(360deg) } }
      `}</style>

      <Toast toast={toast} />
      <ProcessPayoutModal
        payout={pendingPayout}
        onConfirm={handleProcessPayout}
        onCancel={() => {
          if (!processingId) setPendingPayout(null);
        }}
        processing={!!processingId}
      />
      <FinanceDetailModal
        item={detailItem}
        type={detailType}
        onClose={() => { setDetailItem(null); setDetailType(null); }}
      />

      <PageHeader
        breadcrumb="Dashboard > Finance > Payouts & Refunds"
        title="Payouts & Refunds"
        subtitle="Manage scholar payouts, reimbursements, and customer refunds."
        onRefresh={fetchData}
      />

      {/* Stats */}
      <div style={statsGrid}>
        {[
          {
            label: "Pending Payouts",
            val: `₹${pendingPayoutsTotal.toLocaleString()}`,
            sub: `${payouts.filter((p) => p.status === "PENDING").length} awaiting`,
            color: "#f59e0b",
          },
          {
            label: "Processed Payouts",
            val: `₹${processedTotal.toLocaleString()}`,
            sub: `${payouts.filter((p) => p.status === "PROCESSED").length} processed`,
            color: "#10b981",
          },
          {
            label: "Reimbursements",
            val: `₹${reimbursements.reduce((a, c) => a + c.amount, 0).toLocaleString()}`,
            sub: `${reimbursements.length} issued`,
            color: "#8b5cf6",
          },
          {
            label: "Total Refunds",
            val: `₹${refundsList.reduce((a, c) => a + c.amount, 0).toLocaleString()}`,
            sub: `${refundsList.length} processed`,
            color: "#ef4444",
          },
        ].map((s, i) => (
          <div key={i} style={cardStyle}>
            <div style={labelStyle}>{s.label}</div>
            <div style={{ ...valueStyle, color: s.color }}>{s.val}</div>
            <div style={subTextStyle}>{s.sub}</div>
          </div>
        ))}
      </div>

      {/* Tab navigation */}
      <div
        style={{
          display: "flex",
          borderBottom: "1px solid #e2e8f0",
          marginBottom: 24,
          gap: 10,
        }}
      >
        {tab("payouts", `📤 Scholar Payouts (${payouts.length})`)}
        {tab("reimbursements", `💸 Reimbursements (${reimbursements.length})`)}
        {tab("refunds", `↩ User Refunds (${refundsList.length})`)}
        {tab("penalties", `⚠️ Scholar Penalties (${penaltiesList.length})`)}
      </div>

      {/* ── Payouts Tab ── */}
      {activeTab === "payouts" && (
        <div style={cardStyle}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 12,
              marginBottom: 20,
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            <div>
              <h3 style={{ ...sectionTitle, marginBottom: 2 }}>
                Scholar Payout Requests
              </h3>
            </div>
            <select
              style={smallSelect}
              value={payoutFilter}
              onChange={(e) => {
                setPayoutFilter(e.target.value);
                setPayoutPage(1);
              }}
            >
              <option value="">All Status</option>
              <option value="PENDING">Pending</option>
              <option value="PROCESSED">Processed</option>
              <option value="REJECTED">Rejected</option>
              <option value="FAILED">Failed</option>
            </select>
          </div>

          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr
                style={{
                  textAlign: "left",
                  color: "#64748b",
                  fontSize: 12,
                  borderBottom: "2px solid #f1f5f9",
                }}
              >
                <th style={thStyle}>PAYOUT ID</th>
                <th style={thStyle}>SCHOLAR</th>
                <th style={thStyle}>NET AMOUNT</th>
                <th style={thStyle}>STATUS</th>
                <th style={thStyle}>DATE</th>
                <th style={{ ...thStyle, textAlign: "right" }}>ACTION</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan={6}
                    style={{
                      padding: 60,
                      textAlign: "center",
                      color: "#94a3b8",
                    }}
                  >
                    Loading payouts…
                  </td>
                </tr>
              ) : pagedPayouts.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    style={{
                      padding: 60,
                      textAlign: "center",
                      color: "#94a3b8",
                    }}
                  >
                    No payouts found.
                  </td>
                </tr>
              ) : (
                pagedPayouts.map((p: any) => {
                  const sc = payoutStatusColor(p.status);
                  const isProcessing = processingId === p.id;
                  return (
                    <tr
                      key={p.id}
                      style={{
                        borderBottom: "1px solid #f8fafc",
                        transition: "background 0.12s",
                        cursor: "pointer",
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.background = "#f8fafc")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.background = "transparent")
                      }
                      onClick={() => { setDetailItem(p); setDetailType("payout"); }}
                    >
                      <td style={tdStyle}>
                        <span
                          style={{
                            fontFamily: "monospace",
                            fontWeight: 700,
                            fontSize: 12,
                            color: "#334155",
                          }}
                        >
                          #{p.id?.slice(0, 10).toUpperCase()}
                        </span>
                      </td>
                      <td style={tdStyle}>
                        <div style={{ fontWeight: 700, fontSize: 14 }}>
                          {p.scholar?.user?.name || "Scholar"}
                        </div>
                      </td>
                      <td style={tdStyle}>
                        <div
                          style={{
                            fontWeight: 800,
                            fontSize: 15,
                            color: "#2563eb",
                          }}
                        >
                          ₹{(p.amount || 0).toLocaleString()}
                        </div>
                        {p.originalAmount && p.originalAmount > p.amount ? (
                          <div style={{ fontSize: 11, color: "#d97706", fontWeight: 600 }}>
                            Req: ₹{p.originalAmount.toLocaleString()}
                          </div>
                        ) : null}
                        <div style={{ fontSize: 11, color: "#94a3b8" }}>
                          Fee: ₹{(p.processingFee || 0).toLocaleString()}
                        </div>
                      </td>
                      <td style={tdStyle}>
                        <span
                          style={{
                            ...badgeStyle,
                            background: sc.bg,
                            color: sc.color,
                          }}
                        >
                          {p.status}
                        </span>
                      </td>
                      <td style={tdStyle}>
                        <span style={{ fontSize: 12, color: "#64748b" }}>
                          {p.createdAt
                            ? new Date(p.createdAt).toLocaleDateString(
                                "en-IN",
                                { day: "2-digit", month: "short" },
                              )
                            : "—"}
                        </span>
                      </td>
                      <td style={{ ...tdStyle, textAlign: "right" }}>
                        {p.status === "PENDING" ? (
                          <button
                            onClick={(e) => { e.stopPropagation(); setPendingPayout(p); }}
                            disabled={isProcessing}
                            style={{
                              border: "none",
                              background: isProcessing
                                ? "#93c5fd"
                                : "linear-gradient(135deg,#2563eb,#3b82f6)",
                              color: "#fff",
                              fontWeight: 700,
                              fontSize: 12,
                              padding: "7px 14px",
                              borderRadius: 10,
                              cursor: isProcessing ? "not-allowed" : "pointer",
                            }}
                          >
                            {isProcessing ? "…" : "Process"}
                          </button>
                        ) : (
                          <span style={{ fontSize: 12, color: "#94a3b8" }}>
                            —
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
          <Paginator
            total={filteredPayouts.length}
            page={payoutPage}
            pageSize={pageSize}
            onPage={setPayoutPage}
          />
        </div>
      )}

      {/* ── Reimbursements Tab ── */}
      {activeTab === "reimbursements" && (
        <div style={cardStyle}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 12,
              marginBottom: 20,
              alignItems: "center",
            }}
          >
            <div>
              <h3 style={{ ...sectionTitle, marginBottom: 2 }}>
                Scholar Reimbursements
              </h3>
              <p style={{ fontSize: 12, color: "#94a3b8", margin: 0 }}>
                For cancelled bookings.
              </p>
            </div>
            <button
              style={{
                background: "#8b5cf6",
                color: "#fff",
                border: "none",
                padding: "8px 16px",
                borderRadius: 8,
                fontWeight: 600,
                cursor: "pointer",
              }}
              onClick={() =>
                showToast("Navigate to Bookings to create a reimbursement.")
              }
            >
              + New Reimbursement
            </button>
          </div>

          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr
                style={{
                  textAlign: "left",
                  color: "#64748b",
                  fontSize: 12,
                  borderBottom: "2px solid #f1f5f9",
                }}
              >
                <th style={thStyle}>ID</th>
                <th style={thStyle}>SCHOLAR</th>
                <th style={thStyle}>BOOKING / SERVICE</th>
                <th style={thStyle}>AMOUNT</th>
                <th style={thStyle}>DATE</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan={5}
                    style={{
                      padding: 60,
                      textAlign: "center",
                      color: "#94a3b8",
                    }}
                  >
                    Loading…
                  </td>
                </tr>
              ) : pagedReimbursements.length === 0 ? (
                <tr>
                  <td
                    colSpan={5}
                    style={{
                      padding: 60,
                      textAlign: "center",
                      color: "#94a3b8",
                    }}
                  >
                    No reimbursements found.
                  </td>
                </tr>
              ) : (
                pagedReimbursements.map((r: any) => (
                  <tr key={r.id} style={{ borderBottom: "1px solid #f8fafc", cursor: "pointer" }}
                    onClick={() => { setDetailItem(r); setDetailType("reimbursement"); }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = "#f8fafc")}
                    onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                  >
                    <td style={tdStyle}>
                      <span
                        style={{
                          fontFamily: "monospace",
                          fontWeight: 700,
                          fontSize: 12,
                        }}
                      >
                        #{r.id?.slice(0, 8)}
                      </span>
                    </td>
                    <td style={tdStyle}>
                      <div style={{ fontWeight: 700, fontSize: 14 }}>
                        {r.scholar?.user?.name}
                      </div>
                    </td>
                    <td style={tdStyle}>
                      <div style={{ fontSize: 12 }}>
                        #{r.booking?.id?.slice(0, 8)}
                      </div>
                      <div style={{ fontSize: 12, color: "#64748b" }}>
                        {r.booking?.service?.name}
                      </div>
                    </td>
                    <td style={tdStyle}>
                      <span
                        style={{
                          fontWeight: 700,
                          fontSize: 14,
                          color: "#8b5cf6",
                        }}
                      >
                        ₹{(r.amount || 0).toLocaleString()}
                      </span>
                    </td>
                    <td style={tdStyle}>
                      <span style={{ fontSize: 12, color: "#64748b" }}>
                        {r.createdAt
                          ? new Date(r.createdAt).toLocaleDateString("en-IN")
                          : "—"}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          <Paginator
            total={reimbursements.length}
            page={reimbursementPage}
            pageSize={pageSize}
            onPage={setReimbursementPage}
          />
        </div>
      )}

      {/* ── Refunds Tab ── */}
      {activeTab === "refunds" && (
        <div style={cardStyle}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 12,
              marginBottom: 20,
              alignItems: "center",
            }}
          >
            <div>
              <h3 style={{ ...sectionTitle, marginBottom: 2 }}>User Refunds</h3>
            </div>
            <button
              style={{
                background: "#ef4444",
                color: "#fff",
                border: "none",
                padding: "8px 16px",
                borderRadius: 8,
                fontWeight: 600,
                cursor: "pointer",
              }}
              onClick={() =>
                showToast("Navigate to Bookings to initiate a refund.")
              }
            >
              + Initiate Refund
            </button>
          </div>

          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr
                style={{
                  textAlign: "left",
                  color: "#64748b",
                  fontSize: 12,
                  borderBottom: "2px solid #f1f5f9",
                }}
              >
                <th style={thStyle}>REFUND ID</th>
                <th style={thStyle}>BOOKING / USER</th>
                <th style={thStyle}>TYPE</th>
                <th style={thStyle}>AMOUNT</th>
                <th style={thStyle}>STATUS</th>
                <th style={thStyle}>DATE</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan={6}
                    style={{
                      padding: 60,
                      textAlign: "center",
                      color: "#94a3b8",
                    }}
                  >
                    Loading…
                  </td>
                </tr>
              ) : pagedRefunds.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    style={{
                      padding: 60,
                      textAlign: "center",
                      color: "#94a3b8",
                    }}
                  >
                    No refunds found.
                  </td>
                </tr>
              ) : (
                pagedRefunds.map((r: any) => {
                  const sc = payoutStatusColor(r.status);
                  return (
                    <tr
                      key={r.id}
                      style={{ borderBottom: "1px solid #f8fafc", cursor: "pointer" }}
                      onClick={() => { setDetailItem(r); setDetailType("refund"); }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = "#f8fafc")}
                      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                    >
                      <td style={tdStyle}>
                        <div
                          style={{
                            fontFamily: "monospace",
                            fontWeight: 700,
                            fontSize: 12,
                          }}
                        >
                          #{r.id?.slice(0, 8)}
                        </div>
                        {r.razorpayRefundId && (
                          <div style={{ fontSize: 10, color: "#94a3b8" }}>
                            {r.razorpayRefundId}
                          </div>
                        )}
                      </td>
                      <td style={tdStyle}>
                        <div style={{ fontWeight: 700, fontSize: 14 }}>
                          {r.booking?.customer?.user?.name}
                        </div>
                        <div style={{ fontSize: 12, color: "#64748b" }}>
                          #{r.booking?.id?.slice(0, 8)}
                        </div>
                      </td>
                      <td style={tdStyle}>
                        <span style={badgeStyle}>
                          {getRefundTypeLabel(r.refundType)}
                        </span>
                        {r.customPercentage ? (
                          <span style={{ fontSize: 11, marginLeft: 6 }}>
                            {r.customPercentage}%
                          </span>
                        ) : null}
                      </td>
                      <td style={tdStyle}>
                        <span
                          style={{
                            fontWeight: 700,
                            fontSize: 14,
                            color: "#ef4444",
                          }}
                        >
                          ₹{(r.amount || 0).toLocaleString()}
                        </span>
                      </td>
                      <td style={tdStyle}>
                        <span
                          style={{
                            ...badgeStyle,
                            background: sc.bg,
                            color: sc.color,
                          }}
                        >
                          {r.status}
                        </span>
                      </td>
                      <td style={tdStyle}>
                        <span style={{ fontSize: 12, color: "#64748b" }}>
                          {r.createdAt
                            ? new Date(r.createdAt).toLocaleDateString("en-IN")
                            : "—"}
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
          <Paginator
            total={refundsList.length}
            page={refundPage}
            pageSize={pageSize}
            onPage={setRefundPage}
          />
        </div>
      )}

      {/* ── Penalties Tab ── */}
      {activeTab === "penalties" && (
        <div style={cardStyle}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 12,
              marginBottom: 20,
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            <div>
              <h3 style={{ ...sectionTitle, marginBottom: 2 }}>
                Scholar Cancellation Penalties
              </h3>
              <p style={{ margin: 0, fontSize: 13, color: "#64748b" }}>
                Tier-based cancellation penalties levied on scholars for short-notice cancellations or no-shows.
              </p>
            </div>
          </div>

          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr
                style={{
                  textAlign: "left",
                  color: "#64748b",
                  fontSize: 12,
                  borderBottom: "2px solid #f1f5f9",
                }}
              >
                <th style={thStyle}>SCHOLAR</th>
                <th style={thStyle}>REASON / SERVICE</th>
                <th style={thStyle}>PENALTY AMOUNT</th>
                <th style={thStyle}>STATUS</th>
                <th style={thStyle}>DATE</th>
                <th style={{ ...thStyle, textAlign: "right" }}>ACTION</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} style={{ padding: 60, textAlign: "center", color: "#94a3b8" }}>
                    Loading penalties…
                  </td>
                </tr>
              ) : pagedPenalties.length === 0 ? (
                <tr>
                  <td colSpan={6} style={{ padding: 60, textAlign: "center", color: "#94a3b8" }}>
                    No scholar penalties recorded.
                  </td>
                </tr>
              ) : (
                pagedPenalties.map((pen: any) => {
                  const statusColor =
                    pen.status === "UNSETTLED"
                      ? { bg: "#fef2f2", color: "#b91c1c" }
                      : pen.status === "SETTLED"
                      ? { bg: "#f0fdf4", color: "#15803d" }
                      : { bg: "#eff6ff", color: "#1d4ed8" };

                  return (
                    <tr key={pen.id} style={{ borderBottom: "1px solid #f8fafc" }}>
                      <td style={tdStyle}>
                        <div style={{ fontWeight: 700, fontSize: 14 }}>
                          {pen.scholar?.user?.name || "Scholar"}
                        </div>
                        <div style={{ fontSize: 11, color: "#94a3b8" }}>
                          {pen.scholar?.user?.phone || pen.scholar?.user?.email}
                        </div>
                      </td>
                      <td style={tdStyle}>
                        <div style={{ fontWeight: 600, fontSize: 13, color: "#334155" }}>
                          {pen.reason}
                        </div>
                        {pen.booking?.service?.name && (
                          <div style={{ fontSize: 11, color: "#64748b" }}>
                            Service: {pen.booking.service.name}
                          </div>
                        )}
                        {pen.waivedReason && (
                          <div style={{ fontSize: 11, color: "#2563eb", fontStyle: "italic" }}>
                            Waived: {pen.waivedReason}
                          </div>
                        )}
                      </td>
                      <td style={tdStyle}>
                        <span style={{ fontWeight: 800, fontSize: 15, color: "#ef4444" }}>
                          - ₹{(pen.amount || 0).toLocaleString()}
                        </span>
                      </td>
                      <td style={tdStyle}>
                        <span style={{ ...badgeStyle, background: statusColor.bg, color: statusColor.color }}>
                          {pen.status}
                        </span>
                      </td>
                      <td style={tdStyle}>
                        <span style={{ fontSize: 12, color: "#64748b" }}>
                          {pen.createdAt ? new Date(pen.createdAt).toLocaleDateString("en-IN", { day: "2-digit", month: "short" }) : "—"}
                        </span>
                      </td>
                      <td style={{ ...tdStyle, textAlign: "right" }}>
                        {pen.status === "UNSETTLED" ? (
                          <button
                            onClick={() => handleWaivePenalty(pen.id)}
                            style={{
                              border: "1px solid #3b82f6",
                              background: "#eff6ff",
                              color: "#1d4ed8",
                              fontWeight: 700,
                              fontSize: 12,
                              padding: "6px 12px",
                              borderRadius: 8,
                              cursor: "pointer",
                            }}
                          >
                            Waive Fee
                          </button>
                        ) : (
                          <span style={{ fontSize: 12, color: "#94a3b8" }}>—</span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
          <Paginator
            total={penaltiesList.length}
            page={penaltyPage}
            pageSize={pageSize}
            onPage={setPenaltyPage}
          />
        </div>
      )}
    </div>
  );
};

export default PayoutsRefunds;
